'use client';

import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSearch, FiChevronLeft, FiChevronRight, FiX, FiFilter, FiAward, FiLayers } from 'react-icons/fi';
import ProjectCard from '@/components/cards/ProjectCard';

// Updated Interface to include Categories
interface DataCount {
  name: string;
  count: number;
}

interface Props {
  initialProjects: any[]; // Now includes relational metrics
  availableTechs: DataCount[];
  availableCategories: DataCount[]; // New
  activeTech?: string;
  activeCategory?: string; // New
  totalCount: number;
  pagination: {
    currentPage: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export default function ProjectArchiveClient({ 
  initialProjects, 
  availableTechs, 
  availableCategories,
  activeTech,
  activeCategory,
  totalCount,
  pagination 
}: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const params = new URLSearchParams(searchParams);
      if (searchQuery) {
        params.set('q', searchQuery);
        params.set('page', '1');
      } else {
        params.delete('q');
      }
      router.push(`${pathname}?${params.toString()}`, { scroll: false });
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  const updateParams = (key: string, value?: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    router.push(`${pathname}?${params.toString()}`, { scroll: false });
  };

  const changePage = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    router.push(`${pathname}?${params.toString()}`);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8">
      {/* Search Header */}
      <div className="mb-16 space-y-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="space-y-4">
            <h1 className="text-6xl font-black tracking-tighter text-zinc-900 dark:text-white uppercase">
              Archive<span className="text-indigo-600">_</span>
            </h1>
            <p className="text-zinc-500 dark:text-zinc-400 font-mono text-xs uppercase tracking-widest">
              Total_Signals_Detected: {totalCount} // Status: Filterable
            </p>
          </div>
        </div>

        <div className="relative max-w-2xl group">
          <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 group-focus-within:text-indigo-600 transition-colors" />
          <input 
            type="text"
            placeholder="Search systems, outcomes, or stacks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-12 py-5 bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800 rounded-3xl text-sm focus:ring-2 focus:ring-indigo-600 outline-none transition-all"
          />
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-16">
        {/* Sidebar Filters */}
        <aside className="w-full lg:w-72 flex-shrink-0 space-y-12">
          
          {/* Category Filter Section */}
          <section>
            <div className="flex items-center gap-2 mb-6 px-2 text-zinc-400">
              <FiAward size={14} className="text-indigo-500" />
              <h3 className="text-[10px] font-black uppercase tracking-[0.3em]">Classifications</h3>
            </div>
            <div className="flex flex-wrap lg:flex-col gap-2">
               <FilterButton 
                  label="All Classes" 
                  count={totalCount} 
                  active={!activeCategory} 
                  onClick={() => updateParams('category')} 
                />
                {availableCategories.map((cat) => (
                  <FilterButton 
                    key={cat.name}
                    label={cat.name} 
                    count={cat.count} 
                    active={activeCategory === cat.name} 
                    onClick={() => updateParams('category', cat.name)} 
                  />
                ))}
            </div>
          </section>

          {/* Tech Filter Section */}
          <section>
            <div className="flex items-center gap-2 mb-6 px-2 text-zinc-400">
              <FiLayers size={14} />
              <h3 className="text-[10px] font-black uppercase tracking-[0.3em]">Core Stack</h3>
            </div>
            <nav className="flex flex-wrap lg:flex-col gap-2">
                {availableTechs.slice(0, 12).map((tech) => (
                  <FilterButton 
                    key={tech.name}
                    label={tech.name} 
                    count={tech.count} 
                    active={activeTech === tech.name} 
                    onClick={() => updateParams('tech', tech.name)} 
                  />
                ))}
            </nav>
          </section>
        </aside>

        {/* Results Area */}
        <div className="flex-1">
          <AnimatePresence mode="popLayout">
            <motion.div layout className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {initialProjects.map((project) => (
                <motion.div
                  key={project.id}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                >
                  <ProjectCard project={project} />
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>

          {/* Empty State */}
          {initialProjects.length === 0 && (
            <div className="py-32 text-center bg-zinc-50 dark:bg-zinc-900/30 border-2 border-dashed border-zinc-200 dark:border-zinc-800 rounded-[3rem]">
              <p className="text-zinc-500 font-mono text-xs uppercase tracking-[0.4em]">Zero_Matches_In_Registry</p>
            </div>
          )}

          {/* Pagination (Simplified) */}
          {pagination.totalPages > 1 && (
            <div className="mt-20 flex items-center justify-between border-t border-zinc-100 dark:border-zinc-900 pt-10 px-4">
              <button
                disabled={!pagination.hasPrevPage}
                onClick={() => changePage(pagination.currentPage - 1)}
                className="p-4 bg-zinc-100 dark:bg-zinc-900 rounded-2xl disabled:opacity-20 hover:bg-indigo-600 hover:text-white transition-all"
              >
                <FiChevronLeft />
              </button>
              
              <div className="font-mono text-[10px] font-black uppercase tracking-widest">
                Sector {pagination.currentPage} <span className="text-zinc-400">/</span> {pagination.totalPages}
              </div>

              <button
                disabled={!pagination.hasNextPage}
                onClick={() => changePage(pagination.currentPage + 1)}
                className="p-4 bg-zinc-100 dark:bg-zinc-900 rounded-2xl disabled:opacity-20 hover:bg-indigo-600 hover:text-white transition-all"
              >
                <FiChevronRight />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Reuse the FilterButton with slight aesthetic tweaks
function FilterButton({ label, count, active, onClick }: any) {
  return (
    <button
      onClick={onClick}
      className={`group flex items-center justify-between w-full px-5 py-3.5 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${
        active 
          ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-500/20' 
          : 'bg-transparent text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-900'
      }`}
    >
      <span>{label}</span>
      <span className={`px-2 py-0.5 rounded-md font-mono text-[9px] ${
        active ? 'bg-white/20 text-white' : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-400'
      }`}>
        {count}
      </span>
    </button>
  );
}